/**
 * App Sidebar Component
 * Modern, professional design with improved UX
 * Collapsible navigation sidebar with keyboard shortcuts
 */

import { useEffect, useMemo } from 'react'
import { useLocation, useNavigate, Link } from 'react-router-dom'
import {
  LayoutDashboard,
  ClipboardList,
  PlusCircle,
  Users,
  DollarSign,
  LogOut,
  HelpCircle,
  Stethoscope,
  MessageCircle,
  Mail,
} from 'lucide-react'
import { useAuthStore } from '@/store/authStore'
import { useAuth } from '@/hooks/useAuth'
import { useAbility } from '@/ability/AbilityContext'
import { canReadDashboard } from '@/ability/ability'
import { useProfileSwitch } from '@/contexts/ProfileSwitchContext'
import { useProviderProfile } from '@/hooks/useProviderProfile'
import WTDcareLogo from '@/assets/wtdcare-logo.png'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  useSidebar,
} from '@/components/ui/sidebar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Kbd } from '@/components/ui/kbd'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { buildSupportMailtoUrl, buildWhatsAppUrl } from '@/lib/support'
import { cn } from '@/lib/utils'
import { useLocalization } from '@/contexts/LocalizationContext'

interface NavItem {
  label: string
  href: string
  icon: React.ComponentType<{ className?: string }>
  shortcut: string
  caslSubject?: string
  caslAction?: string
}

const dashboardNavItem: NavItem = {
  label: 'nav.dashboard',
  href: '/dashboard',
  icon: LayoutDashboard,
  shortcut: 'D',
  caslSubject: 'Dashboard',
}

const orderNavItems: NavItem[] = [
  {
    label: 'nav.createOrder',
    href: '/create-order',
    icon: PlusCircle,
    shortcut: 'C',
    caslSubject: 'Order',
    caslAction: 'manage',
  },
  {
    label: 'nav.orders',
    href: '/orders',
    icon: ClipboardList,
    shortcut: 'O',
    caslSubject: 'Order',
  },
]

/**
 * Provider-only. Gated on holding a PROVIDER profile rather than CASL — AbilityProvider
 * prefers the ENTITY/ORGANIZATION profile's rules, so a provider-granted rule can be lost.
 */
const myServicesNavItem: NavItem = {
  label: 'nav.myServices',
  href: '/my-services',
  icon: Stethoscope,
  shortcut: 'M',
}

const managementNavItems: NavItem[] = [
  {
    label: 'nav.providers',
    href: '/providers',
    icon: Users,
    shortcut: 'P',
    caslSubject: 'Provider',
  },
  {
    label: 'nav.financials',
    href: '/financials',
    icon: DollarSign,
    shortcut: 'F',
    caslSubject: 'Finance',
  },
]

export function AppSidebar() {
  const location = useLocation()
  const navigate = useNavigate()
  const { logout } = useAuth()
  const user = useAuthStore((state) => state.user)
  const { state } = useSidebar()
  const isCollapsed = state === 'collapsed'
  const { t, isRTL } = useLocalization()
  const ability = useAbility()
  const { canAccessBusinessClientOrderCreation } = useProfileSwitch()
  const { isProvider } = useProviderProfile()

  const visibleMainNavItems = useMemo((): NavItem[] => {
    if (canAccessBusinessClientOrderCreation) {
      return [dashboardNavItem, ...orderNavItems]
    }
    const showDashboard = canReadDashboard(ability)
    const orderItemsWithoutCreate = orderNavItems.filter(
      (item) => item.href !== '/create-order'
    )
    return [
      ...(showDashboard ? [dashboardNavItem] : []),
      ...orderItemsWithoutCreate.filter((item) =>
        item.caslSubject ? ability.can(item.caslAction || 'read', item.caslSubject) : true
      ),
    ]
  }, [ability, canAccessBusinessClientOrderCreation])

  const visibleManagementNavItems = useMemo(
    () => {
      if (canAccessBusinessClientOrderCreation) return []
      const items = managementNavItems.filter((item) =>
        item.caslSubject ? ability.can(item.caslAction || 'read', item.caslSubject) : true
      )
      if (isProvider) items.push(myServicesNavItem)
      return items
    },
    [ability, canAccessBusinessClientOrderCreation, isProvider]
  )

  /**
   * Opens WhatsApp click-to-chat with a prefilled greeting in the active language.
   * Opened in a new tab so the dashboard session stays put.
   */
  const handleWhatsAppClick = () => {
    const message = isRTL
      ? 'أهلاً! أحتاج مساعدة بخصوص لوحة تحكم الأعمال.'
      : 'Hello! I need help with the Business Dashboard.'
    window.open(buildWhatsAppUrl(message), '_blank', 'noopener,noreferrer')
  }

  const handleEmailClick = () => {
    const subject = isRTL
      ? 'طلب دعم – لوحة تحكم الأعمال'
      : 'Support request – Business Dashboard'
    window.location.href = buildSupportMailtoUrl(subject)
  }

  const handleLogout = async () => {
    try {
      await logout()
    } catch (e) {
      console.error('Logout failed:', e)
    } finally {
      navigate('/login', { replace: true })
    }
  }

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return
      }

      if (e.metaKey || e.ctrlKey) {
        return
      }

      const key = e.key.toUpperCase()
      const allNavItems = [...visibleMainNavItems, ...visibleManagementNavItems]
      const navItem = allNavItems.find((item) => item.shortcut === key)
      if (navItem) {
        e.preventDefault()
        navigate(navItem.href)
        return
      }

      if (key === 'L') {
        e.preventDefault()
        handleLogout()
        return
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [navigate, visibleMainNavItems, visibleManagementNavItems])

  return (
    <Sidebar collapsible="icon" className="border-e-0">
      {/* Logo Header */}
      <SidebarHeader className="p-0 border-b-0">
        <div className={cn(
          "flex items-center gap-3 px-4 py-5 transition-all duration-200",
          isCollapsed && "justify-center px-2"
        )}>
          <div className="relative flex-shrink-0">
            <div className="absolute inset-0 bg-gradient-to-br from-brand-primary to-brand-secondary rounded-xl blur-lg opacity-30" />
            <div className="relative flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-brand-primary to-brand-secondary shadow-lg">
              <img
                src={WTDcareLogo}
                alt="WTDcare"
                className="h-6 w-6 object-contain filter brightness-0 invert"
                draggable={false}
              />
            </div>
          </div>
          {!isCollapsed && (
            <div className="flex flex-col">
              <span className="text-base font-bold bg-gradient-to-r from-brand-primary to-brand-secondary bg-clip-text text-transparent">
                WTDcare
              </span>
              <span className="text-[10px] text-gray-500 font-medium tracking-wide uppercase">
                {canAccessBusinessClientOrderCreation
                  ? t('profileSwitch.businessClient')
                  : t('nav.businessPortal')}
              </span>
            </div>
          )}
        </div>
      </SidebarHeader>

      {/* Navigation */}
      <SidebarContent className="px-3 py-2">
        {/* Main Navigation */}
        <SidebarGroup>
          {!isCollapsed && (
            <SidebarGroupLabel className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-2 px-3">
              {t('nav.mainMenu')}
            </SidebarGroupLabel>
          )}
          <SidebarGroupContent>
            <SidebarMenu className="space-y-1">
              {visibleMainNavItems.map((item) => (
                <NavMenuItem
                  key={item.href}
                  item={item}
                  isActive={
                    location.pathname === item.href ||
                    location.pathname.startsWith(item.href + '/')
                  }
                  isCollapsed={isCollapsed}
                />
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {visibleManagementNavItems.length > 0 && !isCollapsed && (
          <SidebarSeparator className="my-4 bg-gray-100" />
        )}

        {visibleManagementNavItems.length > 0 && (
        <SidebarGroup>
          {!isCollapsed && (
            <SidebarGroupLabel className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-2 px-3">
              {t('nav.management')}
            </SidebarGroupLabel>
          )}
          <SidebarGroupContent>
            <SidebarMenu className="space-y-1">
              {visibleManagementNavItems.map((item) => (
                <NavMenuItem
                  key={item.href}
                  item={item}
                  isActive={
                    location.pathname === item.href ||
                    location.pathname.startsWith(item.href + '/')
                  }
                  isCollapsed={isCollapsed}
                />
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        )}
      </SidebarContent>

      {/* Footer */}
      <SidebarFooter className="p-3 border-t-0">
        {/* User Profile Card */}
        {!isCollapsed && user && (
          <div className="mb-3 rounded-xl bg-gradient-to-br from-gray-50 to-gray-100 p-3">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-brand-primary to-brand-secondary text-white font-semibold text-sm shadow-sm">
                {user.name?.charAt(0)?.toUpperCase() || user.phone_number?.charAt(0) || '?'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user.name || t('common.user')}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {user.phone_number || user.email || ''}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <SidebarMenu className="space-y-1">
          {/* Help */}
          <SidebarMenuItem>
            <DropdownMenu>
              {/*
                SidebarMenuButton's own `tooltip` prop wraps the button in a Tooltip root,
                which DropdownMenuTrigger's Slot cannot forward its props through. Compose
                the two triggers by hand instead so both the tooltip and the menu work.
              */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <DropdownMenuTrigger asChild>
                    <SidebarMenuButton
                      className={cn(
                        "w-full rounded-lg transition-all duration-200",
                        "hover:bg-gray-100 text-gray-600 hover:text-gray-900",
                        isCollapsed && "justify-center"
                      )}
                    >
                      <HelpCircle className="h-4 w-4 flex-shrink-0" />
                      {!isCollapsed && <span className="flex-1">{t('nav.helpAndSupport')}</span>}
                    </SidebarMenuButton>
                  </DropdownMenuTrigger>
                </TooltipTrigger>
                <TooltipContent side="right" align="center" hidden={!isCollapsed}>
                  {t('nav.helpAndSupport')}
                </TooltipContent>
              </Tooltip>
              <DropdownMenuContent
                side="top"
                align={isRTL ? 'end' : 'start'}
                className="w-56"
              >
                <DropdownMenuItem onSelect={handleWhatsAppClick} className="gap-2 cursor-pointer">
                  <MessageCircle className="h-4 w-4 text-green-600" />
                  <span>{t('support.chatOnWhatsApp')}</span>
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={handleEmailClick} className="gap-2 cursor-pointer">
                  <Mail className="h-4 w-4 text-gray-500" />
                  <span>{t('support.emailUs')}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>

          {/* Logout */}
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={handleLogout}
              tooltip={
                <span className="flex items-center gap-2">
                  {t('nav.logout')} <Kbd>L</Kbd>
                </span>
              }
              className={cn(
                "w-full rounded-lg transition-all duration-200 group/logout",
                "hover:bg-red-50 text-gray-600 hover:text-red-600",
                isCollapsed && "justify-center"
              )}
            >
              <LogOut className="h-4 w-4 flex-shrink-0 group-hover/logout:text-red-500" />
              {!isCollapsed && (
                <>
                  <span className="flex-1">{t('nav.logout')}</span>
                  <Kbd className="opacity-50 group-hover/logout:opacity-100">L</Kbd>
                </>
              )}
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>

        {/* Version Info */}
        {!isCollapsed && (
          <div className="mt-3 text-center">
            <p className="text-[10px] text-gray-400">
              {t('common.version')}
            </p>
          </div>
        )}
      </SidebarFooter>
    </Sidebar>
  )
}

/**
 * Navigation Menu Item Component
 */
function NavMenuItem({
  item,
  isActive,
  isCollapsed,
}: {
  item: NavItem
  isActive: boolean
  isCollapsed: boolean
}) {
  const Icon = item.icon
  const { t } = useLocalization()

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        isActive={isActive}
        tooltip={
          <span className="flex items-center gap-2">
            {t(item.label)} <Kbd>{item.shortcut}</Kbd>
          </span>
        }
        className={cn(
          "relative w-full rounded-lg transition-all duration-200",
          isActive
            ? "bg-gradient-to-r from-brand-primary to-brand-secondary text-white shadow-md shadow-brand-primary/25 hover:shadow-lg hover:shadow-brand-primary/30"
            : "hover:bg-gray-100 text-gray-600 hover:text-gray-900",
          isCollapsed && "justify-center"
        )}
      >
        <Link to={item.href} className="flex min-w-0 items-center w-full">
          <Icon className={cn(
            "h-4 w-4 flex-shrink-0 transition-transform",
            isActive && "text-white"
          )} />
          {!isCollapsed && (
            <>
              <span className="min-w-0 flex-1 truncate ms-3">{t(item.label)}</span>
              <Kbd className={cn(
                "ms-auto transition-opacity",
                isActive ? "bg-white/20 text-white border-white/30" : "opacity-50"
              )}>
                {item.shortcut}
              </Kbd>
            </>
          )}
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  )
}
